import { Box, CircularProgress, createTheme, CssBaseline, Grid, ThemeProvider, useTheme } from "@mui/material"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { BSIfcDisplayer } from "../../../../../../components/ifc-displayer/BSIfcDisplayer"
import { ViewerTypes } from "../../../../../../components/ifc-displayer/enums/ViewerTypes"
import { TabEnum } from "../../../../../../components/tabs/tabs"
import { pagesUrl } from "../../../../../../core/appConstants"
import { BSBimModelContext } from "../../../../../../core/context/beem-shot/BSBimModel/BSBimModelContext"
import { BSModelFileContext } from "../../../../../../core/context/beem-shot/BSBimModel/BSBimModelFileContext"
import { CodeReferenceContext } from "../../../../../../core/context/code-acv/CodeReferenceContext"
import { AppNavBarContext, NavbarTypeEnum } from "../../../../../../core/context/nav-bar/AppNavBarContext"
import { CodeExtrait } from "../../../../../../core/dto/code-extrait/code-extrait"
import { CodeExtraitDisplay } from "../../../../../../core/dto/code-extrait/CodeExtraitDisplay"
import { useViewerAnalyze } from "../../../../../../core/hooks/viewer/useViewerAnalyze"
import { resolveUrl } from "../../../../../../core/services/http-service"
import { BSBimModelOverview } from "./components/BSBimModelOverview"

export function BSBimModelDetailPage(): React.JSX.Element {
  const navigate = useNavigate()

  const { projectId } = useParams()

  const { bsBimModel } = useContext(BSBimModelContext)
  const { file } = useContext(BSModelFileContext)
  const { setTypeNavBar, setActionOnBackButton, setStepHeader, setTitle } = useContext(AppNavBarContext)
  const { isCodeAcvLoading } = useContext(CodeReferenceContext)

  const [codesExtraits, setCodesExtraits] = useState<CodeExtrait[]>([])
  const [selectedCodeExtrait, setSelectedCodeExtrait] = useState<CodeExtraitDisplay>()

  const { onChangeModel, setProgress, handleClickAcv } = useViewerAnalyze(
    codesExtraits,
    setCodesExtraits,
    selectedCodeExtrait,
    setSelectedCodeExtrait
  )

  useEffect(() => setSelectedCodeExtrait(undefined), [setSelectedCodeExtrait])

  const theme = useTheme()
  const modifiedTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      background: {
        ...theme.palette.background,
        default: "#ffffff",
        paper: "#ffffff",
      },
    },
  })

  const navigateBack: () => void = useCallback(() => {
    navigate(resolveUrl(pagesUrl.PROJECT_PAGE_DETAIL, [projectId], { tab: TabEnum.PROJECT_BIM_MODEL }))
  }, [projectId, navigate])

  useEffect(() => {
    setTypeNavBar(NavbarTypeEnum.STEP_HEADER)
    setActionOnBackButton(navigateBack)
    setStepHeader(0)
    setTitle(bsBimModel?.fileName ?? "")
  }, [navigateBack, setActionOnBackButton, setStepHeader, setTypeNavBar, setTitle, bsBimModel])

  return (
    <ThemeProvider theme={modifiedTheme}>
      <CssBaseline />

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={5} sm={5}>
            {bsBimModel ? <BSBimModelOverview selectedBSBimModel={bsBimModel} /> : <CircularProgress />}
          </Grid>

          <Grid item xs={7}>
            {!isCodeAcvLoading && file ? (
              <BSIfcDisplayer
                file={file}
                onChangeModel={onChangeModel}
                handleSelectedCodeExtrait={handleClickAcv}
                setExternalProgress={setProgress}
                type={ViewerTypes.CODE_VERIFICATION}
                codesExtraits={codesExtraits}
                showFileName
                height={`calc(100vh  - ${BOTTOM_NAVIGATION_HEIGHT} - 140px)`}
              />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <CustomBottomNavigation actionLabel="" cancelLabel="Retour" cancelAction={navigateBack} />
    </ThemeProvider>
  )
}
